import AppModalContainer from '@nxsys/components/AppModalContainer';
import moment from 'moment';
import React from 'react';
import { Col, Row, Card, Tag, Divider, Typography, Space } from 'antd';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { RPN } from '@nxsys/store/slices/rpn/types';
import { useTheme } from 'styled-components';
import {
  UserOutlined,
  FileTextOutlined,
  CalculatorOutlined,
  DollarOutlined,
  CalendarOutlined,
  IdcardOutlined,
  CreditCardOutlined,
  PercentageOutlined,
} from '@ant-design/icons';
import { USC_EXEMPTION_TYPE } from '@nxsys/constants/psr';

const { Title, Text } = Typography;

type RPNModalProps = {
  isOpen: boolean;
  toggle: () => void;
  data: RPN | null;
};

const RPNDetailModal: React.FC<RPNModalProps> = ({
  isOpen,
  toggle,
  data,
}: RPNModalProps) => {
  const { messages, formatMessage } = useTypedIntl();
  const theme = useTheme();

  if (!data) return;

  const sections = [
    {
      title: messages['rpn.employmentDetails'],
      icon: <UserOutlined style={{ color: theme.palette.blue[500] }} />,
      color: theme.palette.blue[500],
      items: [
        {
          label: messages['common.forename'],
          value: data?.rpns?.[0]?.name?.firstName ?? '-',
          icon: <UserOutlined className="text-gray-600" />,
        },
        {
          label: messages['common.surname'],
          value: data?.rpns?.[0]?.name?.familyName ?? '-',
          icon: <UserOutlined className="text-gray-600" />,
        },
        {
          label: messages['rpn.ppsNumber'],
          value: data?.rpns?.[0]?.employeeID?.employeePpsn ?? '-',
          icon: <IdcardOutlined className="text-gray-600" />,
        },
        {
          label: messages['rpn.employmentId'],
          value: data?.rpns?.[0]?.employeeID?.employmentID ?? '-',
          icon: <CreditCardOutlined className="text-gray-600" />,
        },
      ],
    },
    {
      title: 'RPN Details',
      icon: <FileTextOutlined style={{ color: theme.palette.green[6] }} />,
      color: theme.palette.green[6],
      items: [
        {
          label: messages['rpn.rpnDetails'],
          value: data?.rpns?.[0]?.rpnNumber ?? '-',
          icon: <FileTextOutlined className="text-gray-600" />,
        },
        {
          label: messages['rpn.issueDate'],
          value: data?.rpns?.[0]?.rpnIssueDate
            ? moment(data?.rpns?.[0]?.rpnIssueDate, 'YYYY-MM-DD').format(
                'DD/MM/YYYY',
              )
            : '-',
          icon: <CalendarOutlined className="text-gray-600" />,
        },
        {
          label: messages['common.effectiveDate'],
          value: data?.rpns?.[0]?.effectiveDate
            ? moment(data?.rpns?.[0]?.effectiveDate, 'YYYY-MM-DD').format(
                'DD/MM/YYYY',
              )
            : '-',
          icon: <CalendarOutlined className="text-gray-600" />,
        },
        {
          label: messages['common.endDate'],
          value: data?.rpns?.[0]?.endDate
            ? moment(data?.rpns?.[0]?.endDate, 'YYYY-MM-DD').format(
                'DD/MM/YYYY',
              )
            : '-',
          icon: <CalendarOutlined className="text-gray-600" />,
        },
      ],
    },
    {
      title: messages['common.tax'],
      icon: <CalculatorOutlined style={{ color: theme.palette.orange[6] }} />,
      color: theme.palette.orange[6],
      items: [
        {
          label: messages['rpn.calculationBasis'],
          value: data?.rpns?.[0]?.incomeTaxCalculationBasis ?? '-',
          icon: <CalculatorOutlined className="text-gray-600" />,
        },
        {
          label: messages['rpn.annualTaxCredits'],
          value: data?.rpns?.[0]?.yearlyTaxCredits ?? '-',
          icon: <DollarOutlined className="text-gray-600" />,
        },
        {
          label: messages['rpn.annualCutOff'],
          value: (data?.rpns?.[0]?.taxRates ?? []).reduce(
            (sum: number, item: any) => {
              return (
                sum +
                (Object.prototype.hasOwnProperty.call(
                  item,
                  'yearlyRateCutOff',
                ) && typeof item.yearlyRateCutOff === 'number'
                  ? item.yearlyRateCutOff
                  : 0)
              );
            },
            0,
          ),
          icon: <PercentageOutlined className="text-gray-600" />,
        },
      ],
    },
    {
      title: 'USC',
      icon: <DollarOutlined style={{ color: theme.palette.cyan[7] }} />,
      color: theme.palette.cyan[7],
      items: [
        {
          label: messages['rpn.exemption'],
          value: data?.rpns?.[0]?.uscStatus
            ? data?.rpns?.[0]?.uscStatus === 'ORDINARY'
              ? 'NOT_EXEMPT'
              : 'EXEMPT'
            : '-',
          icon: <DollarOutlined className="text-gray-600" />,
        },
        {
          label: formatMessage(
            { id: 'rpn.annualCutOffPercent' },
            { percent: 0.5 },
          ),
          value: data?.rpns?.[0]?.uscRates[0]?.yearlyUSCRateCutOff ?? 0,
          icon: <PercentageOutlined className="text-gray-600" />,
        },
        {
          label: formatMessage(
            { id: 'rpn.annualCutOffPercent' },
            { percent: 2 },
          ),
          value: data?.rpns?.[0]?.uscRates[1]?.yearlyUSCRateCutOff ?? 0,
          icon: <PercentageOutlined className="text-gray-600" />,
        },
        {
          label: formatMessage(
            { id: 'rpn.annualCutOffPercent' },
            { percent: 3 },
          ),
          value: data?.rpns?.[0]?.uscRates[2]?.yearlyUSCRateCutOff ?? 0,
          icon: <PercentageOutlined className="text-gray-600" />,
        },
      ],
    },
  ];

  const formatValue = (value: any) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-IE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }
    return value ?? '-';
  };

  const getStatusTag = (value: any) => {
    if (value === USC_EXEMPTION_TYPE.EXEMPT) {
      return <Tag color={theme.palette.success.main}>EXEMPT</Tag>;
    } else if (value === USC_EXEMPTION_TYPE.NOT_EXEMPT) {
      return <Tag color={theme.palette.red[6]}>NOT EXEMPT</Tag>;
    }
    return value;
  };

  return (
    <AppModalContainer
      title={
        <Space direction="vertical" size={0} className="w-full">
          <Title level={4} className="m-0 text-gray-900">
            RPN Details
          </Title>
          <Text type="secondary" className="text-sm text-gray-500">
            Issued {moment(data.rpns?.[0]?.rpnIssueDate).format('D MMMM YYYY')}
          </Text>
        </Space>
      }
      isOpen={isOpen}
      toggle={toggle}
      width={800}
    >
      <div className="py-4">
        <Row gutter={[24, 24]}>
          {sections.map((section, index) => (
            <Col key={index} span={24}>
              <Card
                size="small"
                className="rounded-lg shadow-sm border border-gray-200"
                bodyStyle={{ padding: '20px' }}
              >
                <div className="mb-4">
                  <Space align="center">
                    {section.icon}
                    <Title
                      level={5}
                      className="m-0"
                      style={{ color: section.color }}
                    >
                      {section.title}
                    </Title>
                  </Space>
                  <Divider className="my-3" />
                </div>

                <Row gutter={[16, 12]}>
                  {section?.items?.map((item, _index) => (
                    <Col key={_index} span={12}>
                      <div className="p-3 px-4 bg-gray-50 rounded-md border border-gray-200">
                        <Space align="center" className="mb-1">
                          {item.icon}
                          <Text strong className="text-xs text-gray-900">
                            {item.label}
                          </Text>
                        </Space>
                        <div className="ml-6">
                          {item.label === messages['rpn.exemption'] ? (
                            getStatusTag(item.value)
                          ) : (
                            <Text className="text-sm text-gray-900 font-medium">
                              {formatValue(item.value)}
                            </Text>
                          )}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </AppModalContainer>
  );
};

export default RPNDetailModal;
